import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdAssessment, MdFileDownload, MdPrint } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import { PAYMENTS_UPDATE_EVENT } from './paymentStore';
import {
  fetchPaymentsForDate,
  fetchMembershipSalesForDate,
  fetchGuestPasses,
} from '../../services/receptionist/receptionistApi';
import {
  fetchExpenses,
  fetchPettyCashReport,
  savePettyCashReport,
  fetchCoaZReportExpenses,
  fetchCoaPettyCashLedgerPayments,
} from '../../services/admin/financeLedgerApi';
import { fetchDetailedReportPageRpc } from '../../services/shared/detailedReportRpcApi';
import { getCachedGymId, ensureGymId } from '../../services/receptionist/gymContext';
import {
  getReceptionistBranchFilter,
  isReceptionistPanelScope,
} from '../../services/gym/branchContext';
import { invalidateCache } from '../../services/cache/queryCache';
import { pkTodayKey } from '../../utils/pkTime';
import { loadAdminSettings } from '../admin/settings/settingsData';
import {
  exportDetailedReportCsv,
  exportDetailedReportExcel,
  exportDetailedReportPdf,
  buildDetailedReportPrintHtml,
} from '../admin/DetailedReportExport';
import {
  buildZReport,
  buildPettyCashDetail,
  buildManualPettyCashDetail,
  localDateKey,
  loadManualExpenses,
  loadPettyCashOpening,
  loadPettyCashCoaExpenses,
  loadPettyCashManualRows,
  savePettyCashOpening,
  savePettyCashManualRows,
  savePettyCashLocalBackup,
  mapApiExpenseToManual,
  mergeManualExpenses,
  buildPettyCashAutoManualRows,
  mergePettyCashAutoRows,
  expenseCalendarDay,
} from '../admin/finance/financeData';
import '../owner/ownerPages.css';
import '../admin/finance/finance.css';
import './receptionistPanel.css';

function formatReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-');
}

function formatDisplayDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function formatAmountCell(amount) {
  const n = Number(amount) || 0;
  if (n <= 0) return '—';
  return n.toLocaleString('en-US');
}

function formatPettyAmount(amount, { allowZero = false } = {}) {
  const n = Number(amount) || 0;
  if (!allowZero && n <= 0) return '';
  return n.toLocaleString('en-US');
}

function SaleSectionTable({ section }) {
  const isSpa = section.columns === 'spa';
  const isOxy = section.columns === 'oxybar';
  const codeHeader = isSpa ? 'Sr.No' : 'Membership Code';
  const noteHeader = isSpa ? 'Massager' : 'Trainer';
  const hasRows = Boolean(section.rows?.length);

  return (
    <section className={`admin-z-sale-section${hasRows ? ' has-sales' : ''}`}>
      <div className="admin-z-sale-section-title">
        <span>{section.title}</span>
      </div>

      {isOxy ? (
        <table className="admin-z-sale-table">
          <thead>
            <tr>
              <th>Membership Code</th>
              <th>Name</th>
              <th>Package</th>
              <th>Duration</th>
              <th>Amount</th>
              <th>Mode of Payment</th>
              <th>Rec NO.</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {hasRows ? (
              section.rows.map((row) => (
                <tr key={row.id} className="admin-z-sale-member-row">
                  <td>{row.membershipCode || ''}</td>
                  <td className="admin-z-sale-member-name">{row.name}</td>
                  <td>{row.package}</td>
                  <td>{row.duration && row.duration !== '—' ? row.duration : '—'}</td>
                  <td className="admin-z-sale-amount">{formatAmountCell(row.amount)}</td>
                  <td>{row.modeOfPayment}</td>
                  <td>{row.recNo === '—' ? '' : row.recNo}</td>
                  <td />
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="admin-z-sale-empty">No oxybar sales for this date</td>
              </tr>
            )}
            <tr className="admin-z-sale-total-row">
              <td colSpan={4} className="admin-z-sale-summary-label"><strong>Total</strong></td>
              <td className="admin-z-sale-amount">
                <strong>{section.total > 0 ? formatAmountCell(section.total) : '—'}</strong>
              </td>
              <td colSpan={3} />
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="admin-z-sale-table">
          <thead>
            <tr>
              <th>{codeHeader}</th>
              <th>Name</th>
              <th>Package</th>
              <th>Duration</th>
              <th>Amount</th>
              <th>Mode of Payment</th>
              <th>Rec NO.</th>
              <th>{noteHeader}</th>
            </tr>
          </thead>
          <tbody>
            {hasRows ? (
              section.rows.map((row) => (
                <tr key={row.id} className="admin-z-sale-member-row">
                  <td>{isSpa ? row.srNo : (row.membershipCode || '')}</td>
                  <td className="admin-z-sale-member-name">{row.name}</td>
                  <td>{row.package}</td>
                  <td>{row.duration && row.duration !== '—' ? row.duration : '—'}</td>
                  <td className="admin-z-sale-amount">{formatAmountCell(row.amount)}</td>
                  <td>{row.modeOfPayment}</td>
                  <td>{row.recNo === '—' ? '' : row.recNo}</td>
                  <td>{row.trainer}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="admin-z-sale-empty">No members purchased this sale today</td>
              </tr>
            )}
            <tr className="admin-z-sale-total-row">
              <td colSpan={4} className="admin-z-sale-summary-label"><strong>Total</strong></td>
              <td className="admin-z-sale-amount">
                <strong>{section.total > 0 ? formatAmountCell(section.total) : '—'}</strong>
              </td>
              <td colSpan={3} />
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
}

function sortPettyRowsByPayments(rows, direction) {
  if (!direction) return rows || [];
  return [...(rows || [])].sort((a, b) => {
    const pa = Number(a.payments) || 0;
    const pb = Number(b.payments) || 0;
    if (pa !== pb) return direction === 'asc' ? pa - pb : pb - pa;
    return String(a.description || '').localeCompare(String(b.description || ''));
  });
}

function PettyPaymentsSortHeader({ sortDir, onToggle }) {
  return (
    <th
      className={`admin-z-petty-sort-th${sortDir ? ' is-active' : ''}`}
      onClick={onToggle}
      role="columnheader"
      aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'}
      title="Click to sort payments: low → high, then high → low"
    >
      <span className="admin-z-petty-sort-label">Payments</span>
      {sortDir === 'asc' ? <span className="admin-z-petty-sort-icon" aria-hidden>↑</span> : null}
      {sortDir === 'desc' ? <span className="admin-z-petty-sort-icon" aria-hidden>↓</span> : null}
    </th>
  );
}

function PettyCashDetailTable({ detail, gymTitle, reportDate }) {
  const [paymentSort, setPaymentSort] = useState(null);

  useEffect(() => {
    setPaymentSort(null);
  }, [reportDate]);

  const bodyRows = useMemo(
    () => (detail.rows || []).filter((row) => row.kind !== 'opening' && row.kind !== 'closing'),
    [detail.rows],
  );

  const displayedRows = useMemo(
    () => sortPettyRowsByPayments(bodyRows, paymentSort),
    [bodyRows, paymentSort],
  );

  const togglePaymentSort = () => {
    setPaymentSort((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <div className="owner-card rec-panel-card admin-finance-section admin-z-sale-report admin-z-petty-report" id="z-petty-cash-print">
      <div className="admin-z-sale-header">
        <div className="admin-z-sale-brand">{gymTitle}</div>
        <div className="admin-z-sale-subtitle-row">
          <span className="admin-z-sale-subtitle">PETTY CASH DETAIL</span>
          <span className="admin-z-sale-date">{formatReportDate(reportDate)}</span>
        </div>
      </div>

      <table className="admin-z-sale-table admin-z-petty-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Receipts</th>
            <PettyPaymentsSortHeader sortDir={paymentSort} onToggle={togglePaymentSort} />
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {(detail.rows || []).filter((row) => row.kind === 'opening').map((row) => (
              <tr key={row.id} className="admin-z-sale-footer-total-row">
                <td>{row.date ? formatDisplayDate(row.date) : ''}</td>
                <td className="admin-z-sale-summary-label">
                  <strong>{row.description}</strong>
                </td>
                <td className="admin-z-sale-amount">{formatPettyAmount(row.receipts)}</td>
                <td className="admin-z-sale-amount">{formatPettyAmount(row.payments)}</td>
                <td className="admin-z-sale-amount">
                  <strong>{formatPettyAmount(row.balance, { allowZero: true })}</strong>
                </td>
              </tr>
          ))}

          {displayedRows.map((row) => (
              <tr key={row.id} className="admin-z-sale-member-row">
                <td>{row.date ? formatDisplayDate(row.date) : ''}</td>
                <td className="admin-z-petty-desc">{row.description}</td>
                <td className="admin-z-sale-amount">{formatPettyAmount(row.receipts)}</td>
                <td className="admin-z-sale-amount">{formatPettyAmount(row.payments)}</td>
                <td className="admin-z-sale-amount">
                  <strong>{formatPettyAmount(row.balance, { allowZero: true })}</strong>
                </td>
              </tr>
          ))}

          {(detail.rows || []).filter((row) => row.kind === 'closing').map((row) => (
              <tr key={row.id} className="admin-z-sale-footer-total-row">
                <td>{row.date ? formatDisplayDate(row.date) : ''}</td>
                <td className="admin-z-sale-summary-label"><strong>{row.description}</strong></td>
                <td className="admin-z-sale-amount">
                  <strong>{formatPettyAmount(row.receipts, { allowZero: true })}</strong>
                </td>
                <td className="admin-z-sale-amount">
                  <strong>{formatPettyAmount(row.payments, { allowZero: true })}</strong>
                </td>
                <td className="admin-z-sale-amount">
                  <strong>{formatPettyAmount(row.balance, { allowZero: true })}</strong>
                </td>
              </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ManualPettyCashDetailTable({
  gymTitle,
  reportDate,
  openingInput,
  onOpeningChange,
  openingBalance,
  manualRows,
  onSave,
  onEdit,
  onCancelEdit,
  isEditing = true,
  canCancel = false,
  saving = false,
  saveMessage = '',
  loading = false,
}) {
  const [paymentSort, setPaymentSort] = useState(null);

  useEffect(() => {
    setPaymentSort(null);
  }, [reportDate]);

  const detail = useMemo(
    () => buildManualPettyCashDetail({
      date: reportDate,
      openingBalance,
      manualRows,
    }),
    [reportDate, openingBalance, manualRows],
  );

  const balanceById = useMemo(() => {
    const map = new Map();
    (detail.rows || []).forEach((row) => {
      if (row.id) map.set(row.id, row.balance);
    });
    return map;
  }, [detail.rows]);

  const displayedRows = useMemo(
    () => sortPettyRowsByPayments(manualRows, paymentSort),
    [manualRows, paymentSort],
  );

  const togglePaymentSort = () => {
    setPaymentSort((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <div
      className={`owner-card rec-panel-card admin-finance-section admin-z-sale-report admin-z-petty-report${isEditing ? '' : ' is-view-only'}`}
      id="z-petty-cash-print"
    >
      <div className="admin-z-sale-header">
        <div className="admin-z-sale-brand">{gymTitle}</div>
        <div className="admin-z-sale-subtitle-row">
          <span className="admin-z-sale-subtitle">PETTY CASH DETAIL</span>
          <span className="admin-z-sale-date">{formatReportDate(reportDate)}</span>
        </div>
      </div>

      <table className="admin-z-sale-table admin-z-petty-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Receipts</th>
            <PettyPaymentsSortHeader sortDir={paymentSort} onToggle={togglePaymentSort} />
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr className="admin-z-sale-footer-total-row">
            <td />
            <td className="admin-z-sale-summary-label"><strong>Opening Balance</strong></td>
            <td className="admin-z-sale-amount" />
            <td className="admin-z-sale-amount" />
            <td className="admin-z-sale-amount">
              {isEditing ? (
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="owner-form-input admin-z-petty-input admin-z-petty-input-amount no-print"
                  value={openingInput}
                  onChange={(e) => onOpeningChange?.(e.target.value)}
                  aria-label="Opening balance"
                />
              ) : null}
              <strong className={isEditing ? 'admin-z-petty-print-only' : ''}>
                {formatPettyAmount(openingBalance, { allowZero: true })}
              </strong>
            </td>
          </tr>

          {(displayedRows || []).map((row) => (
              <tr key={row.id} className="admin-z-sale-member-row">
                <td>{formatDisplayDate(row.date || reportDate)}</td>
                <td className="admin-z-petty-desc">{row.description || '—'}</td>
                <td className="admin-z-sale-amount">{formatPettyAmount(row.receipts)}</td>
                <td className="admin-z-sale-amount">{formatPettyAmount(row.payments)}</td>
                <td className="admin-z-sale-amount">
                  <strong>{formatPettyAmount(balanceById.get(row.id) ?? openingBalance, { allowZero: true })}</strong>
                </td>
              </tr>
          ))}

          {!(manualRows || []).length && (
            <tr className="no-print">
              <td colSpan={5} className="admin-z-sale-empty">
                {loading ? 'Loading petty cash…' : 'No cash sales or ledger expenses for this date.'}
              </td>
            </tr>
          )}

          <tr className="admin-z-sale-grand-row">
            <td />
            <td className="admin-z-sale-summary-label"><strong>Closing Balance</strong></td>
            <td className="admin-z-sale-amount">
              <strong>{formatPettyAmount(detail.totalReceipts, { allowZero: true })}</strong>
            </td>
            <td className="admin-z-sale-amount">
              <strong>{formatPettyAmount(detail.totalPayments, { allowZero: true })}</strong>
            </td>
            <td className="admin-z-sale-amount">
              <strong>{formatPettyAmount(detail.closingBalance, { allowZero: true })}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="admin-z-petty-save-bar no-print">
        {isEditing ? (
          <>
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Petty Cash'}
            </button>
            {canCancel ? (
              <button
                type="button"
                className="owner-btn owner-btn-secondary"
                onClick={onCancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
            ) : null}
          </>
        ) : (
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={onEdit}
          >
            Edit Petty Cash
          </button>
        )}
        {saveMessage ? (
          <span className="owner-placeholder-text">{saveMessage}</span>
        ) : null}
      </div>
    </div>
  );
}

function openDetailedReportPrint(html) {
  const triggerPrint = (win) => {
    const run = () => {
      try {
        win.focus();
        win.print();
      } catch {
        // ignore
      }
    };
    win.onload = run;
    window.setTimeout(run, 400);
  };

  const win = window.open('', '_blank');
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    triggerPrint(win);
    return true;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Detailed Report Print');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return false;
  }
  doc.open();
  doc.write(html);
  doc.close();
  const iframeWin = iframe.contentWindow;
  if (iframeWin) triggerPrint(iframeWin);
  window.setTimeout(() => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }, 60_000);
  return true;
}

function isSubscriptionSource(p) {
  const src = String(p?.source || '').toLowerCase().replace(/-/g, '_');
  return src === 'subscription'
    || src === 'membership'
    || src === 'new_membership'
    || src === 'subscriptions';
}

function isRenewalMembershipRow(row) {
  const src = String(row?.source || '').toLowerCase().replace(/-/g, '_');
  if (src === 'renewal_sale' || src === 'renewal') return true;
  if (/\brenewal\b/i.test(String(row?.package || row?.package_name || ''))) return true;
  const note = String(row?.paymentNote || row?.payment_note || '');
  return /(^|[·|]\s*)(Renewed|Complimentary renewal)\b/i.test(note);
}

function mergeDetailedReportPayments(paymentRows, membershipSales) {
  const nonNewMembershipPayments = (paymentRows || []).filter((p) => !isSubscriptionSource(p));
  const newMembershipRows = (membershipSales || []).filter((row) => !isRenewalMembershipRow(row));
  const coveredPaymentIds = new Set(
    newMembershipRows.map((r) => String(r.rawId || r.id || '')).filter((id) => id && id !== '—' && !String(id).startsWith('mem-')),
  );
  const orphanSubscriptionPayments = (paymentRows || []).filter((p) => {
    if (!isSubscriptionSource(p)) return false;
    const pid = String(p.rawId || p.id || '');
    if (pid && coveredPaymentIds.has(pid)) return false;
    return true;
  });
  return [
    ...nonNewMembershipPayments,
    ...newMembershipRows,
    ...orphanSubscriptionPayments,
  ];
}

export default function ReceptionistZReport({
  title = 'Z Report',
  subtitle = 'Sale report by package, then petty cash detail for the day',
  badgeLabel = 'Daily close',
  /** Admin Detailed Report: manual petty cash rows (add / edit). */
  manualPettyCash = false,
} = {}) {
  const toast = usePanelToast();
  const receptionistScope = isReceptionistPanelScope();
  const todayPk = pkTodayKey();
  const [reportDate, setReportDate] = useState(() => (
    isReceptionistPanelScope() ? pkTodayKey() : localDateKey()
  ));
  const [gymId, setGymId] = useState(() => getCachedGymId() || '');
  const [payments, setPayments] = useState([]);
  const [guestPasses, setGuestPasses] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [openingInput, setOpeningInput] = useState('0');
  const [manualRows, setManualRows] = useState([]);
  const [savingPetty, setSavingPetty] = useState(false);
  const [pettySaveMessage, setPettySaveMessage] = useState('');
  const [isEditingPetty, setIsEditingPetty] = useState(true);
  const [hasSavedPetty, setHasSavedPetty] = useState(false);
  const pettyDraftRef = useRef(null);
  const loadIdRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const gymTitle = useMemo(() => {
    const settings = loadAdminSettings(gymId);
    return (
      settings?.gym?.name?.trim()
      || settings?.softwareName?.trim()
      || 'OXYGYM HEALTH & FITNESS CLUB'
    ).toUpperCase();
  }, [gymId]);

  useEffect(() => {
    if (receptionistScope && reportDate !== todayPk) {
      setReportDate(todayPk);
    }
  }, [receptionistScope, reportDate, todayPk]);

  const loadData = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    const date = receptionistScope
      ? pkTodayKey()
      : String(reportDate || localDateKey()).slice(0, 10);
    const stillCurrent = () => loadId === loadIdRef.current;

    try {
      setError('');
      setLoading(true);
      // Avoid showing the previous day's petty rows under the new date
      if (manualPettyCash) {
        setManualRows([]);
        setOpeningBalance(0);
        setOpeningInput('0');
      }

      const gid = getCachedGymId() || await ensureGymId().catch(() => '');
      if (!stillCurrent()) return;
      if (gid) setGymId(gid);

      const branch = receptionistScope ? (getReceptionistBranchFilter() || '') : '';
      let paymentRows = [];
      let membershipSales = [];
      let guestRows = [];
      let expenseRows = [];

      const rpc = gid
        ? await fetchDetailedReportPageRpc({
          gymId: gid,
          date,
          branch,
          receptionistScope,
        }).catch((err) => {
          console.warn('Detailed report RPC failed, using fallback', err?.message || err);
          return null;
        })
        : null;

      if (rpc) {
        paymentRows = rpc.payments || [];
        membershipSales = rpc.membershipSales || [];
        guestRows = rpc.guestPasses || [];
        expenseRows = rpc.expenses || [];
      } else {
        const fallback = await Promise.all([
          fetchPaymentsForDate(date).catch(() => []),
          fetchMembershipSalesForDate(date).catch(() => []),
          fetchGuestPasses().catch(() => []),
          fetchExpenses(gid || undefined).catch(() => []),
        ]);
        [paymentRows, membershipSales, guestRows, expenseRows] = fallback;
      }

      const [coaExpenseRows, coaPettyRows] = await Promise.all([
        fetchCoaZReportExpenses(date, gid || undefined).catch(() => []),
        fetchCoaPettyCashLedgerPayments(date, gid || undefined).catch(() => []),
      ]);
      if (!stillCurrent()) return;

      const mergedPayments = mergeDetailedReportPayments(paymentRows, membershipSales);

      // Same-day expenses only; Z totals use date → cash/bank (see countsTowardZReportExpense)
      const onReportDay = (row) => expenseCalendarDay(row) === date;
      const apiExpenses = (expenseRows || [])
        .map(mapApiExpenseToManual)
        .filter(onReportDay);
      const coaMapped = (coaExpenseRows || [])
        .map((row) => mapApiExpenseToManual(row))
        .filter(onReportDay);
      const coaPettyMapped = (coaPettyRows || [])
        .map((row) => mapApiExpenseToManual(row))
        .filter(onReportDay);
      const localCoa = (loadPettyCashCoaExpenses(gid, date) || []).filter(onReportDay);
      const dayManualExpenses = loadManualExpenses(gid).filter(onReportDay);
      const allExpenses = mergeManualExpenses(
        mergeManualExpenses(
          mergeManualExpenses([...apiExpenses, ...coaMapped], coaPettyMapped),
          localCoa,
        ),
        dayManualExpenses,
      );

      const guestIncome = (guestRows || []).map((g) => ({
        ...g,
        member: g.name || g.member || '—',
        amount: Number(g.amount) || Number(g.fee) || Number(g.price) || 0,
        date: g.startDate || g.date || g.createdAt,
        createdAt: g.createdAt || g.date || '',
        startDate: g.startDate || '',
        method: g.method || g.paymentMethod || 'cash',
        package: 'Guest Pass',
        duration: g.duration || g.passType || '',
        type: 'Guest Pass',
        source: 'guest_pass',
        status: g.status || 'completed',
        invoiceId: g.invoiceId || g.id || '',
        phone: g.phone || '',
      }));

      const openingLocal = loadPettyCashOpening(gid, date);
      let opening = openingLocal;
      let rows = manualPettyCash ? loadPettyCashManualRows(gid, date) : [];
      let saved = false;

      // Always rebuild day's sales + cash CoA ledger lines for petty cash
      const dayZ = buildZReport({
        date,
        payments: mergedPayments,
        guestPassIncome: guestIncome,
        expenses: allExpenses,
        gymId: gid,
      });
      const autoRows = buildPettyCashAutoManualRows({
        date,
        payments: mergedPayments,
        guestPassIncome: guestIncome,
        expenses: allExpenses,
        salesRows: dayZ.salesRows || [],
        openingBalance: opening,
      });

      if (manualPettyCash) {
        try {
          const remote = await fetchPettyCashReport(date, gid);
          if (!stillCurrent()) return;
          const remoteRows = remote?.rows || [];
          const hasRemote = Boolean(
            remote?.id
            || remoteRows.length
            || Number(remote?.openingBalance) > 0,
          );
          if (hasRemote) {
            opening = Number(remote.openingBalance) || 0;
            // Prefer live auto sales/expenses; never keep a blank saved sheet over fresh auto rows
            rows = mergePettyCashAutoRows(
              remoteRows.length ? remoteRows : rows,
              autoRows,
            );
            if (!(rows || []).length && (autoRows || []).length) {
              rows = [...autoRows];
            }
            saved = true;
            savePettyCashOpening(gid, date, opening);
            savePettyCashManualRows(gid, date, rows);
            savePettyCashLocalBackup(date, { openingBalance: opening, rows });
          }
        } catch {
          // Keep local cache if Supabase table is missing / offline
        }
        if (!saved) {
          // Auto-fill sales + cash ledger expense lines; keep editing until user saves
          rows = mergePettyCashAutoRows(rows, autoRows);
        }
        if (!(rows || []).length && (autoRows || []).length) {
          rows = [...autoRows];
        }
        if (saved || (rows || []).length) {
          savePettyCashLocalBackup(date, { openingBalance: opening, rows });
          savePettyCashManualRows(gid, date, rows);
        }
      }

      if (!stillCurrent()) return;

      // Apply sale + petty state together so Petty is never blank while Sale Report has data
      setPayments(mergedPayments);
      setGuestPasses(guestRows || []);
      setExpenses(allExpenses);
      setOpeningBalance(opening);
      setOpeningInput(String(opening || 0));
      if (manualPettyCash) {
        setManualRows(rows || []);
        setHasSavedPetty(saved);
        setIsEditingPetty(!saved);
        pettyDraftRef.current = {
          openingBalance: opening,
          openingInput: String(opening || 0),
          manualRows: (rows || []).map((row) => ({ ...row })),
        };
      }
      setPettySaveMessage('');
    } catch (err) {
      if (!stillCurrent()) return;
      setError(err.message || 'Failed to load Z Report');
    } finally {
      if (stillCurrent()) setLoading(false);
    }
  }, [reportDate, manualPettyCash, receptionistScope]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const refresh = () => {
      const gid = getCachedGymId();
      if (gid) invalidateCache(`gym:${gid}:detailed-report:`);
      loadData();
    };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [loadData]);

  const guestPassIncome = useMemo(
    () => (guestPasses || []).map((g) => ({
      ...g,
      member: g.name || g.member || '—',
      amount: Number(g.amount) || Number(g.fee) || Number(g.price) || 0,
      // Prefer visit start_date for day attribution (imports often have later created_at)
      date: g.startDate || g.date || g.createdAt,
      createdAt: g.createdAt || g.date || '',
      startDate: g.startDate || '',
      method: g.method || g.paymentMethod || 'cash',
      package: 'Guest Pass',
      duration: g.duration || g.passType || '',
      type: 'Guest Pass',
      source: 'guest_pass',
      status: g.status || 'completed',
      invoiceId: g.invoiceId || g.id || '',
      phone: g.phone || '',
    })),
    [guestPasses],
  );

  const zReport = useMemo(() => buildZReport({
    date: reportDate,
    payments,
    guestPassIncome,
    expenses,
    gymId,
    manualExpenses: [],
  }), [reportDate, payments, guestPassIncome, expenses, gymId]);

  const pettyCash = useMemo(() => buildPettyCashDetail({
    date: reportDate,
    payments,
    guestPassIncome,
    expenses,
    // Use the same day-scoped sale rows as SALE REPORT so memberships are not missed
    salesRows: zReport.salesRows || [],
    openingBalance,
  }), [reportDate, payments, guestPassIncome, expenses, zReport.salesRows, openingBalance]);

  const handleOpeningChange = (value) => {
    setOpeningInput(value);
    const amount = Math.max(0, Number(String(value).replace(/,/g, '')) || 0);
    setOpeningBalance(amount);
  };

  const handleOpeningSave = () => {
    const amount = Math.max(0, Number(String(openingInput).replace(/,/g, '')) || 0);
    savePettyCashOpening(gymId, reportDate, amount);
    setOpeningBalance(amount);
    setOpeningInput(String(amount));
  };

  const handlePettyCashSave = async () => {
    const amount = Math.max(0, Number(String(openingInput).replace(/,/g, '')) || 0);
    const rowsSnapshot = (manualRows || []).map((row) => ({ ...row }));
    const isUpdate = hasSavedPetty;
    let gid = gymId || getCachedGymId() || '';
    if (!gid) {
      try {
        gid = await ensureGymId();
      } catch {
        gid = '';
      }
    }
    if (gid && gid !== gymId) setGymId(gid);

    setOpeningBalance(amount);
    setOpeningInput(String(amount));
    savePettyCashOpening(gid, reportDate, amount);
    savePettyCashManualRows(gid, reportDate, rowsSnapshot);
    savePettyCashLocalBackup(reportDate, { openingBalance: amount, rows: rowsSnapshot });
    setSavingPetty(true);
    setPettySaveMessage('');
    try {
      const saved = await savePettyCashReport({
        reportDate,
        openingBalance: amount,
        rows: rowsSnapshot,
      }, gid);
      const confirmedRows = saved?.rows?.length ? saved.rows : rowsSnapshot;
      const confirmedOpening = Number(saved?.openingBalance);
      const openingToUse = Number.isFinite(confirmedOpening) ? confirmedOpening : amount;
      setManualRows(confirmedRows);
      setOpeningBalance(openingToUse);
      setOpeningInput(String(openingToUse));
      savePettyCashOpening(gid, reportDate, openingToUse);
      savePettyCashManualRows(gid, reportDate, confirmedRows);
      savePettyCashLocalBackup(reportDate, { openingBalance: openingToUse, rows: confirmedRows });
      setPettySaveMessage('Saved to database.');
      if (isUpdate) {
        toast.showUpdated('Petty cash report');
      } else {
        toast.showSaved('Petty cash report');
      }
      setHasSavedPetty(true);
      setIsEditingPetty(false);
      pettyDraftRef.current = {
        openingBalance: openingToUse,
        openingInput: String(openingToUse),
        manualRows: confirmedRows,
      };
    } catch (err) {
      const msg = err.message || 'Saved locally only. Run supabase/petty_cash_reports.sql to enable cloud save.';
      setPettySaveMessage(msg);
      toast.showError(msg, 'Saved on this device only');
      // Local cache is still valid for this browser
      setHasSavedPetty(true);
      setIsEditingPetty(false);
      pettyDraftRef.current = {
        openingBalance: amount,
        openingInput: String(amount),
        manualRows: rowsSnapshot,
      };
    } finally {
      setSavingPetty(false);
    }
  };

  const handlePettyCashEdit = () => {
    pettyDraftRef.current = {
      openingBalance,
      openingInput,
      manualRows: (manualRows || []).map((row) => ({ ...row })),
    };
    setIsEditingPetty(true);
    setPettySaveMessage('');
    toast.showSuccess('You can edit opening balance and rows, then save.', 'Edit mode');
  };

  const handlePettyCashCancelEdit = () => {
    const draft = pettyDraftRef.current;
    if (draft) {
      setOpeningBalance(draft.openingBalance);
      setOpeningInput(draft.openingInput);
      setManualRows(draft.manualRows);
      if (gymId) {
        savePettyCashOpening(gymId, reportDate, draft.openingBalance);
        savePettyCashManualRows(gymId, reportDate, draft.manualRows);
      }
    }
    setIsEditingPetty(false);
    setPettySaveMessage('');
  };

  const buildExportPayload = () => ({
    title: manualPettyCash ? 'Detailed Report' : 'Z Report',
    gymTitle,
    reportDate,
    zReport,
    manualPettyCash,
    openingBalance,
    manualRows,
    payments,
    guestPassIncome,
    expenses,
    salesRows: zReport.salesRows || [],
  });

  const handlePrint = () => {
    if (manualPettyCash) {
      handleOpeningSave();
    }
    const ok = openDetailedReportPrint(buildDetailedReportPrintHtml(buildExportPayload()));
    if (!ok) {
      toast.showError('Allow pop-ups to print the report.', 'Print blocked');
    }
  };

  const handleExportCsv = () => {
    exportDetailedReportCsv(buildExportPayload());
    toast.showSuccess('CSV download started.', 'Exported');
  };

  const handleExportExcel = () => {
    exportDetailedReportExcel(buildExportPayload());
    toast.showSuccess('Excel download started.', 'Exported');
  };

  const handleExportPdf = () => {
    const ok = exportDetailedReportPdf(buildExportPayload());
    if (ok === false) {
      toast.showError('Allow pop-ups to open the PDF export window.', 'Export blocked');
      return;
    }
    toast.showSuccess('PDF preview opened — use Print → Save as PDF if needed.', 'Exported');
  };

  return (
    <div className="rec-z-report">
      <PageHeader
        title={title}
        subtitle={subtitle}
        variant="banner"
      >
        <span className="owner-badge owner-badge-info" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <MdAssessment aria-hidden />
          {badgeLabel}
        </span>
      </PageHeader>

      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading Z Report…</p>}

      <div className="owner-card rec-panel-card admin-finance-section admin-z-sale-toolbar no-print">
        <div className="admin-finance-member-header">
          <h2 className="owner-card-title">Report date</h2>
          <div className="report-toolbar-actions">
            <div>
              <label className="owner-form-label" htmlFor="rec-zreport-date">Date</label>
              <input
                id="rec-zreport-date"
                type="date"
                className="owner-form-input"
                value={receptionistScope ? todayPk : reportDate}
                readOnly={receptionistScope}
                disabled={receptionistScope}
                onChange={(e) => {
                  if (receptionistScope) return;
                  setReportDate(e.target.value || localDateKey());
                }}
              />
            </div>
            {!manualPettyCash && (
              <>
                <div>
                  <label className="owner-form-label" htmlFor="rec-petty-opening">Opening Balance (manual)</label>
                  <input
                    id="rec-petty-opening"
                    type="number"
                    min="0"
                    step="1"
                    className="owner-form-input"
                    value={openingInput}
                    onChange={(e) => setOpeningInput(e.target.value)}
                    onBlur={handleOpeningSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleOpeningSave();
                      }
                    }}
                  />
                </div>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={handleOpeningSave}>
                  Save Opening
                </button>
              </>
            )}
            <button type="button" className="owner-btn owner-btn-secondary" onClick={handlePrint}>
              <MdPrint aria-hidden /> Print
            </button>
            <button type="button" className="owner-btn owner-btn-secondary" onClick={handleExportCsv}>
              <MdFileDownload aria-hidden /> Export CSV
            </button>
            <button type="button" className="owner-btn owner-btn-secondary" onClick={handleExportExcel}>
              <MdFileDownload aria-hidden /> Export Excel
            </button>
            <button type="button" className="owner-btn owner-btn-secondary" onClick={handleExportPdf}>
              <MdFileDownload aria-hidden /> Export PDF
            </button>
          </div>
        </div>
      </div>

      <div id="z-detailed-report-print">
      <div className="owner-card rec-panel-card admin-finance-section admin-z-sale-report" id="z-sale-report-print">
        <div className="admin-z-sale-header">
          <div className="admin-z-sale-brand">{gymTitle}</div>
          <div className="admin-z-sale-subtitle-row">
            <span className="admin-z-sale-subtitle">SALE REPORT</span>
            <span className="admin-z-sale-date">{formatReportDate(zReport.date)}</span>
          </div>
        </div>

        <div className="admin-z-sale-sections-grid">
          {(zReport.saleReportSections || []).map((section) => (
            <SaleSectionTable key={section.id} section={section} />
          ))}
        </div>

        <div className="admin-z-sale-section admin-z-sale-grand-wrap">
          <table className="admin-z-sale-table">
            <tbody>
              <tr className="admin-z-sale-grand-row">
                <td colSpan={4} className="admin-z-sale-summary-label"><strong>Grand Total</strong></td>
                <td className="admin-z-sale-amount">
                  <strong>
                    {formatAmountCell(
                      zReport.saleReportGrandTotal
                      ?? zReport.saleReportTotalAmount
                      ?? 0,
                    )}
                  </strong>
                </td>
                <td colSpan={3} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {manualPettyCash ? (
        <ManualPettyCashDetailTable
          gymTitle={gymTitle}
          reportDate={reportDate}
          openingInput={openingInput}
          onOpeningChange={handleOpeningChange}
          openingBalance={openingBalance}
          manualRows={manualRows}
          onSave={handlePettyCashSave}
          onEdit={handlePettyCashEdit}
          onCancelEdit={handlePettyCashCancelEdit}
          isEditing={isEditingPetty}
          canCancel={hasSavedPetty}
          saving={savingPetty}
          saveMessage={pettySaveMessage}
          loading={loading}
        />
      ) : (
        <PettyCashDetailTable
          detail={pettyCash}
          gymTitle={gymTitle}
          reportDate={reportDate}
        />
      )}
      </div>
    </div>
  );
}
